// @ai-generated 2026-10-08T20:07:16+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
import { Permission } from '@Constants/Permission.g';
import { useHasPermission } from '@Contexts/appContext';
import { type ApiContainerAction, type ApiContainerType, EnumContainerActionStatus } from '@Model';
import { FrameEditContainerType } from '@Modules/Stammdaten';
import {
    type AsyncStateUpdate,
    isOk,
    LinkedField,
    useGuiBinder,
    useLinkedField,
    useLinkProperty,
    useTranslate,
} from '@synqo/frontend-component-library';
import { type ContainerTypeChange } from './useContainerTypeChange';

interface ContainerTypeCellProps {
    action: ApiContainerAction;
    /** Path to the container actions of the order; the row's own segment is appended here. */
    baseApiObjectPath: string;
    canEdit: boolean;
    /** Whether the completed order is being corrected, which opens the type of a booked row too. */
    correcting?: boolean;
    requestUpdateState: AsyncStateUpdate<ApiContainerAction>;
    autoOpen?: boolean;
    otherActionIds?: readonly string[];
    typeChange?: ContainerTypeChange;
}

/**
 * The container type of one planned row, which the user links after planning it.
 *
 * Linking another type takes the container off the row, because that was picked for the old type. A planned
 * row may change its type, and a booked one while the order is corrected; a marked row's booking is about it.
 */
// @ai-generated 2026-10-08T20:07:16+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function ContainerTypeCell({
    action,
    baseApiObjectPath,
    canEdit,
    correcting = false,
    requestUpdateState,
    autoOpen,
    otherActionIds = [],
    typeChange,
}: ContainerTypeCellProps) {
    const i18n = useTranslate('');
    const hasPermission = useHasPermission();

    const isOpen =
        action.booking_status === EnumContainerActionStatus.Planned ||
        (correcting && action.booking_status === EnumContainerActionStatus.Booked);

    const binder = useGuiBinder<ApiContainerAction>({
        data: action,
        meta: undefined,
        canEdit: canEdit && isOpen,
        requestUpdateState,
    });
    const linkContainerType = useLinkedField<ApiContainerAction, ApiContainerType>(
        `${baseApiObjectPath}/${action.id}`,
        'container_type',
        FrameEditContainerType,
        hasPermission(Permission.StammdatenContainerTypeCreate),
        requestUpdateState,
    );
    const { linkApi } = linkContainerType;
    const currentTypeId = action.container_type.foreign_key;
    const containerTypeField = useLinkProperty(binder, {
        ...linkApi,
        link: async (containerTypeId) => {
            const linked = await linkApi.link(containerTypeId);
            if (isOk(linked) && typeChange && otherActionIds.length > 0) {
                await typeChange.changeType(otherActionIds, containerTypeId);
            }

            return linked;
        },
        unlink: async () => {
            const unlinked = await linkApi.unlink();
            if (isOk(unlinked) && typeChange && currentTypeId && otherActionIds.length > 0) {
                await typeChange.clearType(otherActionIds, currentTypeId);
            }

            return unlinked;
        },
    });

    return (
        <LinkedField<ApiContainerType>
            {...containerTypeField}
            {...linkContainerType.events}
            label={undefined}
            placeholder={i18n('Containertyp wählen …')}
            autoFocusOnRender={autoOpen}
        />
    );
}
